import { not, and } from 'tio-ui/utilities';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import type { TemplateFieldName } from '../../utils/tuition-assistance/fields.ts';
import type { TasFieldOption as TasFieldOptionModel } from 'tio-data/data/resources/tas-field-option.type';
import type { TasProgramTemplateExtension as TasProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';

import {
  getConfigForTasField,
  getSortedOptionsByFieldName,
} from '../../utils/tuition-assistance/fields.ts';
import { uniqueId } from '@ember/helper';
import { cached } from '@glimmer/tracking';
import type Owner from '@ember/owner';

export interface TuitionAssistanceProgramFormFieldSignature {
  Args: {
    name: TemplateFieldName;
    programTemplate: TasProgramTemplateModel;
    hideRequiredLabel?: boolean;
    labelClass?: string;
  };
  Blocks: {
    default: [
      {
        isVisible: boolean;
        isRequired: boolean;
        programLabel: string;
        isFieldOptionPicklist: boolean | undefined;
        fieldOptions: ReadonlyArray<TasFieldOptionModel['value']>;
        inputId: string;
        name: TemplateFieldName;
        programTemplate: TasProgramTemplateModel;
        isTouched: boolean;
        markTouched: () => void;
      },
    ];
  };
  Element: HTMLDivElement;
}

export default class TuitionAssistanceProgramFormFieldComponent extends Component<TuitionAssistanceProgramFormFieldSignature> {
  @tracked hasBeenTouched = false;
  constructor(owner: Owner, args: TuitionAssistanceProgramFormFieldSignature['Args']) {
    super(owner, args);
    if (this.fieldConfig.value_as_field_option && !args.programTemplate.tasFieldOptions?.length) {
      console.error(
        `Program template ${args.programTemplate.id} has a field option picklist for field ${args.name} but no field options.`
      );
    }
  }

  get isTouched() {
    return this.hasBeenTouched;
  }

  markTouched = () => {
    this.hasBeenTouched = true;
  };

  @cached
  get fieldConfig() {
    return getConfigForTasField(this.args.name, this.args.programTemplate.fields);
  }

  @cached
  get fieldOptions() {
    // For now, we are only supporting raw values as field options.
    // Return to this for support for labels which differ from the value.
    // This will also require refactoring the instances where we use FormSelect
    // with these yielded options since that pattern will need changing.
    // - Julia 12/14/2023
    return this.fieldConfig.value_as_field_option
      ? getSortedOptionsByFieldName(this.args.name, this.args.programTemplate.tasFieldOptions).map(
          (option) => option.value
        )
      : [];
  }

  <template>
    {{#let
      (hash
        isVisible=this.fieldConfig.visible
        isRequired=this.fieldConfig.required
        programLabel=this.fieldConfig.label
        isFieldOptionPicklist=this.fieldConfig.value_as_field_option
        inputId=(uniqueId)
        isTouched=this.isTouched
        markTouched=this.markTouched
      )
      as |config|
    }}
      {{#if config.isVisible}}
        <div ...attributes>
          <label
            for={{config.inputId}}
            class="tio-text-sm-semibold tio-fg-secondary block mb-1 text-balance
              {{if @labelClass @labelClass}}"
          >
            {{config.programLabel}}
            {{#if (and config.isRequired (not @hideRequiredLabel))}}
              <span class="tio-fg-danger">*</span>
            {{/if}}
          </label>
          {{yield
            (hash
              isVisible=config.isVisible
              isRequired=config.isRequired
              programLabel=config.programLabel
              isFieldOptionPicklist=config.isFieldOptionPicklist
              fieldOptions=this.fieldOptions
              inputId=config.inputId
              name=@name
              programTemplate=@programTemplate
              isTouched=config.isTouched
              markTouched=config.markTouched
            )
          }}
        </div>
      {{/if}}
    {{/let}}
  </template>
}
